/**
 * Local TTS Hook
 *
 * React hook that provides streaming TTS via a local server (e.g., Kokoro).
 * Designed as a drop-in alternative to useStreamingTTS for edge-local deployments.
 *
 * The hook accumulates LLM tokens into complete sentences, fetches each
 * sentence's PCM audio from the local TTS server, and pipes it to
 * AudioStreamPlayer.
 *
 * A reply plays as one turn, in sentence order (SA-1392):
 * - at most MAX_IN_FLIGHT sentences are being synthesized at once, started in
 *   sentence order; a sentence's audio joins the player only after the
 *   previous sentence's has (a promise chain), so a short sentence that
 *   synthesizes first still waits its turn;
 * - the player is told "no more audio" once per reply, at the end of the
 *   chain, by flush() (the normal end) or finish() (the end without the
 *   unspoken tail), so a gap between sentences never ends the turn;
 * - a generation number moves on interrupt, prepareForNewStream, reset and
 *   disconnect; each of them aborts every request in flight, and anything
 *   from an older generation is dropped.
 *
 * Each reply has its own player and AudioContext; prepareForNewStream closes
 * the previous one, and only the current player reports (SA-1397).
 */

import { useRef, useCallback, useEffect, useState } from 'react'
import { LocalTTSService } from '@/services/LocalTTSService'
import {
  AudioStreamPlayer,
  type AudioPlayerState,
} from '@/services/AudioStreamPlayer'
import { StreamingTextProcessor, type ProcessedText } from '@/utils/streamingSanitization'

/**
 * Two keeps the one-at-a-time server busy. More would hold the browser's
 * HTTP/1.1 connections to the host (about six) while sentences wait their
 * turn on the server, and stall the app's own /api calls.
 */
const MAX_IN_FLIGHT = 2

interface WaitingSentence {
  text: string
  settle: (pcm: ArrayBuffer | null) => void
}

export interface UseLocalTTSOptions {
  /** Voice to use for synthesis */
  voice: string
  /** Whether local TTS is enabled */
  enabled: boolean
  /** Base path for local TTS server */
  basePath?: string
  /** Sample rate of TTS output */
  sampleRate?: number
  /** Pre-buffer duration in milliseconds */
  preBufferMs?: number
  /** Callback when TTS starts speaking */
  onStart?: () => void
  /** Callback when TTS stops speaking */
  onEnd?: () => void
  /** Callback on error */
  onError?: (error: Error) => void
}

export interface UseLocalTTSReturn {
  isEnabled: boolean
  isConnected: boolean
  isSpeaking: boolean
  /**
   * The reply's voice is on its way but not playing: the player is buffering,
   * or a sentence was sent for synthesis and its audio has not joined the
   * player yet (SA-1397).
   */
  isBuffering: boolean
  playbackState: AudioPlayerState
  processToken: (token: string) => void
  /** End the reply normally: the buffered tail is spoken, then the turn ends. */
  flush: () => void
  /** End the reply without the buffered tail: what was sent plays, then the turn ends. */
  finish: () => void
  interrupt: () => void
  connect: () => Promise<void>
  disconnect: () => Promise<void>
  reset: () => void
  prepareForNewStream: () => Promise<void>
  /** Track TTS character count (for usage reporting) */
  characterCount: number
}

export function useLocalTTS(options: UseLocalTTSOptions): UseLocalTTSReturn {
  const {
    voice,
    enabled,
    basePath = '/local-tts',
    sampleRate = 24000,
    preBufferMs = 100,
    onStart,
    onEnd,
    onError,
  } = options

  const [playbackState, setPlaybackState] = useState<AudioPlayerState>('idle')
  const [characterCount, setCharacterCount] = useState(0)

  const ttsServiceRef = useRef<LocalTTSService | null>(null)
  const audioPlayerRef = useRef<AudioStreamPlayer | null>(null)
  const textProcessorRef = useRef<StreamingTextProcessor | null>(null)
  const textBufferRef = useRef('')
  const hasStartedRef = useRef(false)
  // The current reply has ended its turn (onEnd fired); cleared with hasStartedRef (SA-1402)
  const turnEndedRef = useRef(false)
  const isReadyRef = useRef(false)

  const generationRef = useRef(0)
  const chainRef = useRef<Promise<void>>(Promise.resolve())
  const waitingRef = useRef<WaitingSentence[]>([])
  const inFlightRef = useRef(0)
  // Sentences of the current reply whose audio has not joined the player.
  const onTheWayRef = useRef(0)
  const [onTheWay, setOnTheWay] = useState(false)

  // Refs for callbacks, so the player made once per reply calls the latest ones
  const onStartRef = useRef(onStart)
  const onEndRef = useRef(onEnd)
  const onErrorRef = useRef(onError)
  useEffect(() => {
    onStartRef.current = onStart
    onEndRef.current = onEnd
    onErrorRef.current = onError
  }, [onStart, onEnd, onError])

  const isSpeaking = playbackState === 'playing'
  const isBuffering =
    playbackState === 'buffering' || (onTheWay && playbackState !== 'playing')

  const getTTSService = useCallback(() => {
    if (!ttsServiceRef.current) {
      ttsServiceRef.current = new LocalTTSService({ basePath, voice, sampleRate })
    }
    return ttsServiceRef.current
  }, [basePath, voice, sampleRate])

  /**
   * End the turn once per reply, from whichever the player reports first:
   * idle after playing, or playback complete with nothing played (SA-1402).
   */
  const endTurn = useCallback(() => {
    if (turnEndedRef.current) return
    turnEndedRef.current = true
    hasStartedRef.current = false
    onEndRef.current?.()
  }, [])

  const getAudioPlayer = useCallback(() => {
    if (!audioPlayerRef.current) {
      // Only the current player reports: one replaced or released (its late
      // `idle` after `destroy()`) never moves the new reply (SA-1397).
      const player: AudioStreamPlayer = new AudioStreamPlayer(
        { sampleRate, channels: 1, preBufferMs },
        {
          onStateChange: (state) => {
            if (audioPlayerRef.current !== player) return
            setPlaybackState(state)
            if (state === 'playing' && !hasStartedRef.current) {
              hasStartedRef.current = true
              onStartRef.current?.()
            }
            if (state === 'idle' && hasStartedRef.current) {
              endTurn()
            }
          },
          onError: (error) => {
            if (audioPlayerRef.current !== player) return
            console.error('[useLocalTTS] Audio player error:', error)
            onErrorRef.current?.(error)
          },
          onPlaybackComplete: () => {
            if (audioPlayerRef.current !== player) return
            endTurn()
          },
        }
      )
      audioPlayerRef.current = player
    }
    return audioPlayerRef.current
  }, [sampleRate, preBufferMs, endTurn])

  const getTextProcessor = useCallback(() => {
    if (!textProcessorRef.current) {
      textProcessorRef.current = new StreamingTextProcessor()
    }
    return textProcessorRef.current
  }, [])

  const reportError = useCallback((gen: number, error: unknown) => {
    if (gen !== generationRef.current) return
    if ((error as Error)?.name === 'AbortError') return
    console.error('[useLocalTTS] Synthesis error:', error)
    onErrorRef.current?.(error as Error)
  }, [])

  /**
   * Start waiting sentences' requests, oldest first, while a place is free.
   */
  const pump = useCallback(() => {
    const gen = generationRef.current
    while (inFlightRef.current < MAX_IN_FLIGHT && waitingRef.current.length > 0) {
      const sentence = waitingRef.current.shift()!
      inFlightRef.current++
      getTTSService()
        .synthesizeToBuffer(sentence.text, voice)
        .then(
          (pcm) => sentence.settle(pcm),
          (error) => {
            reportError(gen, error)
            sentence.settle(null)
          }
        )
        .finally(() => {
          if (gen !== generationRef.current) return
          inFlightRef.current--
          pump()
        })
    }
  }, [getTTSService, voice, reportError])

  /**
   * Take a sentence: fetch it when a place is free, play it after the one before.
   */
  const accept = useCallback(
    (text: string) => {
      const gen = generationRef.current
      const audio = new Promise<ArrayBuffer | null>((settle) => {
        waitingRef.current.push({ text, settle })
      })
      onTheWayRef.current++
      if (onTheWayRef.current === 1) setOnTheWay(true)
      pump()
      chainRef.current = chainRef.current.then(async () => {
        try {
          const pcm = await audio
          if (gen !== generationRef.current || !pcm || pcm.byteLength === 0) return
          const player = getAudioPlayer()
          // prepareForNewStream initializes the player; connect() does not.
          if (player.getAudioContextState() === null) await player.initialize()
          if (gen !== generationRef.current) return
          player.enqueuePCM(pcm)
        } catch (error) {
          reportError(gen, error)
        } finally {
          // Queued, empty or failed, this sentence is no longer on its way.
          if (gen === generationRef.current) {
            onTheWayRef.current--
            if (onTheWayRef.current === 0) setOnTheWay(false)
          }
        }
      })
    },
    [pump, getAudioPlayer, reportError]
  )

  /**
   * Tell the player "no more audio" once every sentence before has joined it.
   */
  const end = useCallback(() => {
    const gen = generationRef.current
    chainRef.current = chainRef.current.then(() => {
      if (gen === generationRef.current) audioPlayerRef.current?.markComplete()
    })
  }, [])

  /**
   * Drop everything from the current reply: abort its requests, release its
   * waiting sentences, and start a fresh chain.
   */
  const newGeneration = useCallback(() => {
    generationRef.current++
    ttsServiceRef.current?.cancel()
    for (const sentence of waitingRef.current) sentence.settle(null)
    waitingRef.current = []
    inFlightRef.current = 0
    onTheWayRef.current = 0
    setOnTheWay(false)
    chainRef.current = Promise.resolve()
  }, [])

  /**
   * Process an LLM token. Buffers text until a sentence boundary,
   * then sends the sentence for synthesis.
   */
  const processToken = useCallback(
    (token: string) => {
      if (!enabled || !isReadyRef.current) return

      const processor = getTextProcessor()
      const items: ProcessedText[] = processor.processToken(token)

      for (const item of items) {
        textBufferRef.current += item.text
        setCharacterCount((c) => c + item.text.length)

        // Send to TTS at sentence boundaries (flush hint)
        if (item.flush && textBufferRef.current.trim()) {
          const text = textBufferRef.current
          textBufferRef.current = ''
          accept(text)
        }
      }
    },
    [enabled, getTextProcessor, accept]
  )

  const flush = useCallback(() => {
    if (!enabled || !isReadyRef.current) {
      // Interrupted or not ready: nothing more is spoken.
      textBufferRef.current = ''
      textProcessorRef.current = null
      end()
      return
    }

    // flush() returns a string of remaining buffered text
    const remaining: string = getTextProcessor().flush()
    if (remaining) {
      textBufferRef.current += remaining
      setCharacterCount((c) => c + remaining.length)
    }

    if (textBufferRef.current.trim()) {
      const text = textBufferRef.current
      textBufferRef.current = ''
      accept(text)
    }
    end()
  }, [enabled, getTextProcessor, accept, end])

  const finish = useCallback(() => {
    textBufferRef.current = ''
    textProcessorRef.current = null
    end()
  }, [end])

  const interrupt = useCallback(() => {
    newGeneration()
    // Silent until the next prepareForNewStream (or connect).
    isReadyRef.current = false
    audioPlayerRef.current?.interrupt()
    textBufferRef.current = ''
    textProcessorRef.current = null
    hasStartedRef.current = false
    turnEndedRef.current = false
  }, [newGeneration])

  const connect = useCallback(async () => {
    isReadyRef.current = true
  }, [])

  const disconnect = useCallback(async () => {
    isReadyRef.current = false
    newGeneration()
    audioPlayerRef.current?.interrupt()
  }, [newGeneration])

  const reset = useCallback(() => {
    newGeneration()
    textBufferRef.current = ''
    hasStartedRef.current = false
    turnEndedRef.current = false
    setCharacterCount(0)
    textProcessorRef.current = null
    audioPlayerRef.current?.interrupt()
  }, [newGeneration])

  const prepareForNewStream = useCallback(async () => {
    newGeneration()
    // Stop the previous reply's playback while its player still reports, then
    // release it and close its AudioContext: one context per reply (SA-1397).
    // The close is not awaited, so the new player starts in this gesture.
    const previous = audioPlayerRef.current
    previous?.interrupt()
    audioPlayerRef.current = null
    previous?.destroy().catch(() => {})
    // Create a fresh AudioStreamPlayer for the new response
    // (avoids stale state from previous playback)
    textBufferRef.current = ''
    hasStartedRef.current = false
    turnEndedRef.current = false
    setCharacterCount(0)
    textProcessorRef.current = null
    // Initialize new player + AudioContext during user gesture
    const player = getAudioPlayer()
    await player.initialize()
    isReadyRef.current = true
  }, [getAudioPlayer, newGeneration])

  // Leaving the chat mid-reply stops the voice: once the voice bar is gone
  // nothing else could. The stream may keep sending tokens; they find the
  // hook not ready and are dropped.
  useEffect(
    () => () => {
      newGeneration()
      isReadyRef.current = false
      hasStartedRef.current = false
      turnEndedRef.current = false
      const player = audioPlayerRef.current
      audioPlayerRef.current = null
      player?.destroy().catch(() => {})
    },
    [newGeneration]
  )

  return {
    isEnabled: enabled,
    isConnected: isReadyRef.current,
    isSpeaking,
    isBuffering,
    playbackState,
    processToken,
    flush,
    finish,
    interrupt,
    connect,
    disconnect,
    reset,
    prepareForNewStream,
    characterCount,
  }
}
